import React from "react";
import "./ui.css";
import {
  disciplineOf,
  type Discipline,
  type DisciplineId,
} from "../utils/disciplines";

/**
 * Ikony disciplín – náboj z boku, jak ho střelec zná z ruky:
 *  - malorážka (.22 LR): rovná nábojnice s okrajem („rantlík") a kulatou olověnou střelou,
 *  - kulovnice: lahvicovitá nábojnice bez okraje (jen drážka pro vytahovač),
 *    tělo širší než krček, špičatá střela.
 * Kreslí se v currentColor, takže přebírají barvu disciplíny.
 */
const ICONS: Record<DisciplineId, React.ReactNode> = {
  MALORAZKA: (
    <>
      {/* okraj – širší než nábojnice */}
      <rect x="1" y="2" width="2.6" height="10" rx="0.8" />
      {/* nábojnice */}
      <rect x="3.4" y="3.2" width="15.2" height="7.6" rx="0.6" />
      {/* olověná střela s kulatou špičkou */}
      <path d="M18.4 3.9 H23.5 C27.2 3.9 29.6 5.2 30.8 7 C29.6 8.8 27.2 10.1 23.5 10.1 H18.4 Z" />
    </>
  ),
  KULOVNICE: (
    <>
      {/* dno bez okraje + drážka pro vytahovač */}
      <rect x="1" y="3" width="1.9" height="8" rx="0.5" />
      <rect x="2.9" y="4.1" width="1.1" height="5.8" />
      {/* tělo nábojnice */}
      <rect x="4" y="3" width="10.6" height="8" rx="0.4" />
      {/* rameno a krček */}
      <path d="M14.6 3 L17.6 4.7 V9.3 L14.6 11 Z" />
      <rect x="17.5" y="4.7" width="4.1" height="4.6" />
      {/* špičatá střela */}
      <path d="M21.5 4.7 C26 4.7 29.3 5.9 31 7 C29.3 8.1 26 9.3 21.5 9.3 Z" />
    </>
  ),
};

export const DisciplineIcon: React.FC<{
  id: DisciplineId;
  /** výška v px, šířka se dopočítá (poměr 32:14) */
  height?: number;
  style?: React.CSSProperties;
}> = ({ id, height = 14, style }) => (
  <svg
    viewBox="0 0 32 14"
    height={height}
    width={Math.round((height * 32) / 14)}
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    style={{ display: "inline-block", verticalAlign: "-0.15em", flexShrink: 0, ...style }}
  >
    {ICONS[id]}
  </svg>
);

interface DisciplineBadgeProps {
  /** cokoli s polem discipline – závod, registrace, výsledek */
  race: { discipline?: string | null } | null | undefined;
  /** sm = úzké seznamy a mobil, md = hlavičky */
  size?: "sm" | "md";
  /** na tmavém/barevném pozadí (zelená hlavička, otevřený panel) */
  onDark?: boolean;
  /** jen ikona bez textu – do velmi úzkých míst */
  iconOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Jednotný štítek disciplíny pro celou aplikaci: ikona náboje + název,
 * barva z číselníku. Používat všude, kde se objeví závod – seznam závodů,
 * karta na dashboardu, přihláška, přihlášky v adminu, výsledky, tabule.
 */
export const DisciplineBadge: React.FC<DisciplineBadgeProps> = ({
  race,
  size = "sm",
  onDark = false,
  iconOnly = false,
  className,
  style,
}) => {
  const d: Discipline = disciplineOf(race);
  const fontSize = size === "md" ? "0.8rem" : "0.75rem";
  const iconHeight = size === "md" ? 15 : 12;

  const colors = onDark
    ? { color: "#fff", background: "rgba(255,255,255,0.22)", border: "rgba(255,255,255,0.55)" }
    : { color: d.color, background: d.bg, border: d.color };

  return (
    <span
      className={className ? `lks-discipline ${className}` : "lks-discipline"}
      title={`Disciplína: ${d.label}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: iconOnly ? 0 : 5,
        padding: size === "md" ? "3px 9px" : "2px 7px",
        borderRadius: 20,
        fontSize,
        fontWeight: 600,
        lineHeight: 1.25,
        whiteSpace: "nowrap",
        letterSpacing: 0.2,
        color: colors.color,
        background: colors.background,
        border: `1px solid ${colors.border}`,
        ...style,
      }}
    >
      <DisciplineIcon id={d.id} height={iconHeight} />
      {!iconOnly && d.label}
    </span>
  );
};

export default DisciplineBadge;
